/**
 * Stats sidebar (v8-visual). Tracks EUR volume, per-scenario rail heat,
 * top blocked events, and a TPS sparkline (last ~60s).
 * v9-interactive adds: TPS arc gauge, per-bank latency sparklines.
 */

import type { RequestEvent } from './feed.js';
import { eur } from './money.js';

// 40 samples × ~1500ms poll ≈ 60s rolling window
const TPS_BUF: number[] = new Array(40).fill(0) as number[];
let lastTps = 0;

export function recordTps(tps: number): void {
  lastTps = tps;
  TPS_BUF.push(tps);
  if (TPS_BUF.length > 40) TPS_BUF.shift();
}

export function tpsHistory(): readonly number[] {
  return TPS_BUF;
}

function renderSparkline(el: Element, data: readonly number[]): void {
  const max = Math.max(...data, 0.1);
  el.replaceChildren(
    ...data.map((v) => {
      const bar = document.createElement('span');
      bar.className = 'spark-bar';
      const pct = Math.max(4, Math.round((v / max) * 36));
      bar.style.height = `${pct}px`;
      if (v > max * 0.75) bar.setAttribute('data-hot', 'true');
      return bar;
    }),
  );
}

// ---- TPS arc gauge (v9) ----
const ARC_LEN = 125.7; // half-circumference of circle r=40
const MAX_TPS_DIAL = 10;

export function renderTpsDial(doc: Document, tps: number): void {
  const arc = doc.querySelector('[data-tps-arc]');
  if (!arc) return;
  const pct = Math.min(1, tps / MAX_TPS_DIAL);
  const filled = Math.round(pct * ARC_LEN * 10) / 10;
  const gap = Math.round((ARC_LEN - filled) * 10) / 10;
  arc.setAttribute('stroke-dasharray', `${filled} ${gap}`);
  const color = tps > 8 ? 'var(--red)' : tps > 5 ? 'var(--amber)' : 'var(--accent)';
  (arc as SVGElement).style.stroke = color;

  const valEl = doc.querySelector('[data-tps-val]');
  if (valEl) {
    const tenths = Math.round(tps * 10); // integer tenths — one-decimal display, no float rounding
    valEl.textContent = `${Math.floor(tenths / 10)}.${tenths % 10}`;
  }
}

// ---- Per-bank latency sparklines (v9) ----
const BANKS = ['evaris_es', 'evairi_com'] as const;
const BANK_LAT: Record<string, number[]> = { evaris_es: [], evairi_com: [] };
const BANK_ERR: Record<string, boolean[]> = { evaris_es: [], evairi_com: [] };
const BANK_BUF_MAX = 8;
const seenBankSeqs = new Set<number>();

/** Test-only accessor: the current rolling latency window for one bank (mirrors `tpsHistory`). */
export function bankLatencyHistory(bank: string): readonly number[] {
  return BANK_LAT[bank] ?? [];
}

function updateBankLatency(events: RequestEvent[]): void {
  // Process events newest-first but only those not already seen
  const fresh = events.filter((e) => !seenBankSeqs.has(e.seq));
  if (fresh.length === 0) return;
  // Insert oldest-first into the rolling window
  for (const e of [...fresh].reverse()) {
    seenBankSeqs.add(e.seq); // mark seen either way so a skip is never reprocessed later
    // A skipped row made no HTTP call — it has no real latency or outcome to report, so it
    // must not enter the per-bank latency/error window (would otherwise dilute it toward 0ms
    // once balances drain and skips dominate), mirroring the exclusion the engine itself
    // already applies before events even reach the feed.
    if (e.skipped) continue;
    const b = e.bank;
    if (!BANK_LAT[b]) BANK_LAT[b] = [];
    if (!BANK_ERR[b]) BANK_ERR[b] = [];
    BANK_LAT[b].push(e.latencyMs);
    BANK_ERR[b].push(e.outcome !== 'success');
    if (BANK_LAT[b].length > BANK_BUF_MAX) BANK_LAT[b].shift();
    if (BANK_ERR[b].length > BANK_BUF_MAX) BANK_ERR[b].shift();
  }
  if (seenBankSeqs.size > 500) {
    const first = seenBankSeqs.values().next().value;
    if (first !== undefined) seenBankSeqs.delete(first);
  }
}

function renderBankSparklines(doc: Document): void {
  for (const bank of BANKS) {
    const el = doc.querySelector(`[data-bank-spark="${bank}"]`);
    if (!el) continue;
    const lat = BANK_LAT[bank] ?? [];
    const errArr = BANK_ERR[bank] ?? [];
    if (lat.length === 0) continue;
    const max = Math.max(...lat, 1);
    el.replaceChildren(
      ...lat.map((v, i) => {
        const bar = document.createElement('span');
        bar.className = 'spark-bar bank-spark-bar';
        const pct = Math.max(3, Math.round((v / max) * 28));
        bar.style.height = `${pct}px`;
        if (errArr[i]) bar.setAttribute('data-err', 'true');
        return bar;
      }),
    );
  }
}

export function renderStats(doc: Document, events: RequestEvent[], conservationCents: number | null): void {
  // EUR volume from conservation
  const volEl = doc.querySelector('[data-stat-vol]');
  if (volEl) volEl.textContent = eur(conservationCents);

  // TPS sparkline + arc gauge
  const sparkEl = doc.querySelector('[data-stat-spark]');
  if (sparkEl) renderSparkline(sparkEl, TPS_BUF);
  renderTpsDial(doc, lastTps);

  // Per-bank latency sparklines
  updateBankLatency(events);
  renderBankSparklines(doc);

  // Rail activity heat (top 6 scenarios by count). Declined journeys are not activity.
  const counts = new Map<string, { ok: number; err: number }>();
  for (const e of events) {
    if (e.skipped) continue;
    const c = counts.get(e.scenario) ?? { ok: 0, err: 0 };
    if (e.outcome === 'success') c.ok++;
    else c.err++;
    counts.set(e.scenario, c);
  }
  const heatEl = doc.querySelector('[data-stat-heat]');
  if (heatEl) {
    const rows = [...counts.entries()]
      .sort((a, b) => b[1].ok + b[1].err - (a[1].ok + a[1].err))
      .slice(0, 6);
    const total = events.length || 1;
    heatEl.replaceChildren(
      ...rows.map(([sc, c]) => {
        const n = c.ok + c.err;
        const errPct = n > 0 ? c.err / n : 0;
        const row = doc.createElement('div');
        row.className = 'heat-row';
        const label = doc.createElement('span');
        label.className = 'heat-label';
        label.textContent = sc.replace(/_/g, ' ');
        const barWrap = doc.createElement('div');
        barWrap.className = 'heat-bar';
        const fill = doc.createElement('div');
        fill.className = `heat-fill${errPct > 0.2 ? ' heat-err' : errPct > 0.05 ? ' heat-warn' : ''}`;
        fill.style.width = `${Math.min(100, Math.round((n / total) * 250))}%`;
        barWrap.append(fill);
        const cnt = doc.createElement('span');
        cnt.className = 'heat-count';
        cnt.textContent = String(n);
        row.append(label, barWrap, cnt);
        return row;
      }),
    );
  }

  // Top blocked (last 3 sanctions or fraud blocks)
  const blocked = events
    .filter(
      (e) =>
        e.scenario === 'sanctions_screen' ||
        e.scenario === 'fraud_check' ||
        e.detail?.includes('BLOCK') ||
        e.detail?.includes('MATCH'),
    )
    .slice(0, 3);
  const blockedEl = doc.querySelector('[data-stat-blocked]');
  if (blockedEl) {
    if (blocked.length === 0) {
      blockedEl.replaceChildren();
      blockedEl.textContent = 'No blocks yet this run';
    } else {
      blockedEl.replaceChildren(
        ...blocked.map((e) => {
          const item = doc.createElement('div');
          item.className = 'blocked-item';
          const icon = e.scenario === 'sanctions_screen' ? '⛔' : '🔴';
          item.textContent = `${icon} ${e.description.slice(0, 55)}`;
          return item;
        }),
      );
    }
  }
}
